import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'November 3, 2026';

export default function TermsOfService() {
  useDocumentTitle('Terms of Service');
  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        lineHeight: 1.7,
        color: '#1a1a1a',
        background: '#fafafa',
        padding: '2rem 1rem',
        minHeight: '100vh',
      }}
    >
      <div
        className="pl-legal"
        style={{
          maxWidth: 720,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 12,
          padding: 'clamp(1.25rem, 6vw, 3rem)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.25rem', color: '#111' }}>
          Terms of Service
        </h1>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '2rem' }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <p>
          These Terms of Service ("Terms") govern your use of Paywall Lab ("the Service", "we",
          "us", or "our"), a third-party application available on the Whop platform. By installing
          or using Paywall Lab, you ("you" or "User") agree to be bound by these Terms.
        </p>

        <h2>1. Service Description</h2>
        <p>
          Paywall Lab provides A/B testing and paid content tools for Whop creators. The Service
          allows you to:
        </p>
        <ul>
          <li>
            Create experiments that test different checkout pricing configurations for your Whop
            products
          </li>
          <li>Route visitors to different pricing variants based on configurable traffic splits</li>
          <li>
            View analytics including conversion rates, revenue metrics, and statistical analysis to
            determine winning variants
          </li>
          <li>
            Sell one-time access to images you upload ("unlock offers") through a shareable link,
            where buyers see a blurred preview and unlock the original after paying through Whop
            checkout
          </li>
        </ul>

        <h2>2. Whop Platform</h2>
        <p>
          Paywall Lab operates as a third-party application on the Whop platform. By using the
          Service:
        </p>
        <ul>
          <li>
            You acknowledge that{' '}
            <strong>
              Whop is not liable for any fault in the Paywall Lab application or any harm that may
              result from its installation or use
            </strong>
          </li>
          <li>
            You acknowledge that Paywall Lab bears sole responsibility for the Service and its
            functionality, and is solely responsible for any liability that may arise from your
            access to or use of the Service
          </li>
          <li>
            You acknowledge that, except where expressly stated by Whop, Whop does not provide
            assistance with the installation or use of Paywall Lab
          </li>
          <li>
            You agree to comply with Whop's Terms of Service and applicable policies in addition to
            these Terms
          </li>
        </ul>

        <h2>3. Eligibility</h2>
        <p>
          You must have an active Whop company account to use Paywall Lab. You represent that you
          have the authority to bind your organization to these Terms.
        </p>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service to conduct fraudulent, deceptive, or misleading pricing practices</li>
          <li>Manipulate experiment data or analytics results</li>
          <li>Use the Service in violation of any applicable laws or regulations</li>
          <li>
            Upload or sell in an unlock offer any content you do not have the rights to, or that
            violates Whop's policies or applicable law
          </li>
          <li>Attempt to access data belonging to other Paywall Lab users</li>
          <li>
            Interfere with, disrupt, or place an unreasonable burden on the Service or its
            infrastructure
          </li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
        </ul>

        <h2>5. Subscription &amp; Billing</h2>
        <p>Paywall Lab currently offers a free tier and a paid Pro plan:</p>
        <ul>
          <li>
            <strong>Free tier:</strong> Your first test, of any kind (links you share, inside your
            whop, partners &amp; ads, or pay-to-unlock), with full results. A test uses this up once
            it has gone live and reached a real visitor
          </li>
          <li>
            <strong>Pro plan:</strong> Currently lets you start further tests. Billed monthly through
            the Whop platform
          </li>
        </ul>
        <p>
          All payments are processed by Whop. Refund policies are governed by Whop's platform terms.
          Subscription tiers, pricing, and included features may evolve over time. We will notify
          existing subscribers through the Whop platform with reasonable notice (typically at least
          30 days) before any change that materially affects an active subscription.
        </p>

        <h2>6. Intellectual Property</h2>
        <ul>
          <li>
            <strong>Our property:</strong> The Service, including its code, design, features, and
            documentation, is owned by Paywall Lab and protected by applicable intellectual property
            laws
          </li>
          <li>
            <strong>Your data:</strong> You retain ownership of your experiment configurations,
            analytics data, unlock offer content, and any content you create using the Service
          </li>
          <li>
            <strong>License to operate the Service:</strong> You grant us a limited, non-exclusive
            license to store, process (including generating blurred previews), and deliver content
            you upload, solely to provide the Service to you and your buyers
          </li>
        </ul>

        <h2>7. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
        <p>We do not warrant that:</p>
        <ul>
          <li>The Service will be uninterrupted, error-free, or secure</li>
          <li>Analytics data or statistical calculations will be perfectly accurate</li>
          <li>The results of any A/B test will lead to improved business outcomes</li>
          <li>The Service will be compatible with all Whop features or configurations</li>
          <li>
            Plans, pricing configurations, checkout settings, or other resources created or modified
            on the Whop platform by the Service on your behalf will be free from errors, or that
            traffic routing between experiment variants will function without interruption
          </li>
          <li>
            Unlock offer content will remain available, or will be delivered to buyers without
            interruption
          </li>
        </ul>
        <p>
          You acknowledge that the Service creates and modifies plans and checkout configurations on
          the Whop platform on your behalf, and you are solely responsible for reviewing and
          monitoring the pricing and configurations applied to your products. You agree that Paywall
          Lab shall not be liable for any revenue loss, overcharges, undercharges, or other
          financial impact resulting from pricing configurations created or modified by the Service.
        </p>

        <h2>8. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PAYWALL LAB AND ITS OPERATORS SHALL NOT
          BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
          INCLUDING BUT NOT LIMITED TO LOSS OF REVENUE, PROFITS, DATA, OR BUSINESS OPPORTUNITIES,
          ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO DAMAGES
          ARISING FROM INCORRECT PRICING CONFIGURATIONS, TRAFFIC ROUTING ERRORS, OR EXPERIMENT
          MALFUNCTIONS.
        </p>
        <p>
          OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL
          NOT EXCEED THE TOTAL AMOUNT YOU PAID TO PAYWALL LAB IN THE TWELVE (12) MONTHS PRECEDING
          THE CLAIM.
        </p>

        <h2>9. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Paywall Lab and its operators from and
          against any claims, damages, losses, liabilities, costs, and expenses (including
          reasonable legal fees) arising from:
        </p>
        <ul>
          <li>Your use of the Service</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any applicable law or regulation</li>
          <li>Any pricing experiments you conduct and their impact on your customers</li>
          <li>
            Any claims by your customers related to pricing, billing, or charges resulting from
            experiments conducted using the Service
          </li>
          <li>
            Content you upload or sell in an unlock offer, including any claim that it infringes a
            third party's rights
          </li>
        </ul>

        <h2>10. Termination</h2>
        <ul>
          <li>
            <strong>By you:</strong> You may stop using the Service and uninstall the app at any
            time through the Whop platform
          </li>
          <li>
            <strong>By us:</strong> We may suspend or terminate your access to the Service if you
            violate these Terms or engage in conduct that we reasonably determine is harmful to the
            Service or other users
          </li>
          <li>
            <strong>Effect of termination:</strong> Upon termination, your data will be handled in
            accordance with our{' '}
            <Link to="/privacy" style={{ color: '#2563eb', textDecoration: 'none' }}>
              Privacy Policy
            </Link>
            . You may request deletion of your data at any time, including after uninstalling the
            app, and we will honor verifiable requests within 30 days
          </li>
        </ul>

        <h2>11. Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will update the "Effective
          date" at the top of this page when changes are made. Continued use of the Service after
          modifications constitutes acceptance of the updated Terms.
        </p>

        <h2>12. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          California, without regard to conflict of law principles.
        </p>
        <p>
          <strong>Informal resolution.</strong> Before filing any claim, you agree to first attempt
          to resolve the dispute informally by contacting Paywall Lab through the Whop platform. The
          parties will attempt to resolve the dispute through good-faith negotiation for at least
          thirty (30) days before either party may initiate formal proceedings.
        </p>
        <p>
          <strong>Binding arbitration.</strong> If the dispute cannot be resolved informally, any
          claim arising out of or relating to these Terms or the Service shall be resolved through
          final and binding arbitration administered by the American Arbitration Association (AAA)
          under its Commercial Arbitration Rules then in effect. The arbitration shall be conducted
          in San Diego, California, by a single arbitrator. Judgment on the award may be entered in
          any court of competent jurisdiction. Each party shall bear its own costs and attorneys'
          fees, except as otherwise required by applicable law or awarded by the arbitrator.
        </p>
        <p>
          <strong>Class action waiver.</strong> YOU AND PAYWALL LAB AGREE THAT EACH MAY BRING CLAIMS
          AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER
          IN ANY PURPORTED CLASS, COLLECTIVE, REPRESENTATIVE, OR CONSOLIDATED PROCEEDING. The
          arbitrator may not consolidate more than one party's claims and may not preside over any
          form of class or representative proceeding. If this class action waiver is found to be
          unenforceable, then the entirety of this arbitration provision shall be null and void.
        </p>
        <p>
          <strong>Jury trial waiver.</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, YOU
          AND PAYWALL LAB WAIVE ANY RIGHT TO A JURY TRIAL IN ANY PROCEEDING ARISING OUT OF OR
          RELATED TO THESE TERMS OR THE SERVICE.
        </p>
        <p>
          <strong>Exceptions.</strong> Notwithstanding the above, either party may (i) bring an
          individual claim in small claims court for disputes within that court's jurisdiction, or
          (ii) seek injunctive or other equitable relief in a court of competent jurisdiction to
          protect intellectual property rights.
        </p>

        <h2>13. Force Majeure</h2>
        <p>
          Paywall Lab shall not be liable for any failure or delay in performance under these Terms
          to the extent caused by circumstances beyond our reasonable control, including but not
          limited to: outages, failures, or changes in the Whop platform; failures of third-party
          hosting providers, database services, or other infrastructure we depend on; acts of God;
          natural disasters; pandemics; war; civil unrest; government actions or orders;
          cyberattacks or denial-of-service events; and disruptions to internet or
          telecommunications services. Our obligations shall be suspended for the duration of the
          event, and we will use commercially reasonable efforts to resume performance as soon as
          practicable.
        </p>

        <h2>14. Severability</h2>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that provision
          will be limited or eliminated to the minimum extent necessary so that these Terms will
          otherwise remain in full force and effect.
        </p>

        <h2>15. Entire Agreement</h2>
        <p>
          These Terms, together with our{' '}
          <Link to="/privacy" style={{ color: '#2563eb', textDecoration: 'none' }}>
            Privacy Policy
          </Link>
          , constitute the entire agreement between you and Paywall Lab regarding the Service and
          supersede all prior agreements and understandings.
        </p>

        <h2>16. Contact</h2>
        <p>
          If you have questions about these Terms, please contact us through the Whop platform via
          the Paywall Lab app listing.
        </p>

        <div
          style={{
            marginTop: '2.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #eee',
            fontSize: '0.85rem',
            color: '#888',
          }}
        >
          <p>&copy; {new Date().getFullYear()} Paywall Lab. All rights reserved.</p>
          <p>
            <Link
              to="/privacy"
              className="pl-tap"
              style={{ color: '#2563eb', textDecoration: 'none' }}
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
